import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  DemandAdjustmentPreview,
  DemandAdjustmentPreviewRow,
} from "../../types/demand-factor.type";
import {
  TYPE_LABEL,
  UNNAMED_ROW,
  formatDateRange,
  formatMultiplier,
  joinLabels,
  labelsOf,
} from "./constants";
import { TargetNames } from "./targetSummary";

interface PreviewResultProps {
  preview: DemandAdjustmentPreview;
  names: TargetNames;
}

const MISSING_VALUE = "ไม่มี";

/** A row whose type is switched off is listed, greyed, because it does not count. */
const DISABLED_ROW_SX = { "& td": { color: "text.disabled" } };

const nameOrMissing = (code: string | null, nameByCode: Map<string, string>): string =>
  code ? nameByCode.get(code) ?? code : MISSING_VALUE;

const describeProducts = (
  row: DemandAdjustmentPreviewRow,
  productNameByCode: Map<string, string>,
): string =>
  row.target_product_code.length === 0
    ? "ทุกสินค้า"
    : joinLabels(labelsOf(row.target_product_code, productNameByCode));

/** What POST /demand-adjustments/preview answered for one store on one date. */
export default function PreviewResult({ preview, names }: PreviewResultProps) {
  const { customer, rows, all_products: allProducts, products } = preview;

  return (
    <Stack gap={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">
          {customer.customer_code} · {customer.name ?? MISSING_VALUE}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          สาย {customer.route_number ?? MISSING_VALUE} · กลุ่ม{" "}
          {nameOrMissing(customer.group, names.groupNameByCode)} · ช่องทาง{" "}
          {nameOrMissing(customer.channel, names.channelNameByCode)}
        </Typography>
      </Paper>

      {rows.length === 0 ? (
        <Alert severity="info">ไม่มี Demand Factor ที่มีผลกับร้านนี้ในวันที่เลือก (×1)</Alert>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ชื่อ</TableCell>
                <TableCell>ประเภท</TableCell>
                <TableCell>ช่วงวันที่</TableCell>
                <TableCell align="right">ตัวคูณ</TableCell>
                <TableCell>สินค้า</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} sx={row.type_enabled ? undefined : DISABLED_ROW_SX}>
                  <TableCell>
                    {row.name ?? UNNAMED_ROW}
                    {!row.type_enabled && (
                      <Typography variant="caption" display="block">
                        ไม่ถูกคำนวณ (ประเภทปิดอยู่)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{TYPE_LABEL[row.type]}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatDateRange(row.valid_from, row.valid_to)}
                  </TableCell>
                  <TableCell align="right">{formatMultiplier(row.multiplier)}</TableCell>
                  <TableCell>{describeProducts(row, names.productNameByCode)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack gap={0.5}>
            <Typography variant="subtitle2">ตัวคูณรวม</Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">สินค้าทั่วไป</Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatMultiplier(allProducts.multiplier)}
              </Typography>
            </Stack>
            {products.map((product) => (
              <Stack key={product.product_code} direction="row" justifyContent="space-between">
                <Typography variant="body2">
                  {nameOrMissing(product.product_code, names.productNameByCode)}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatMultiplier(product.multiplier)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
